import React, { useEffect, useState, createContext, useContext } from 'react'
import { CookiesProvider as ReactCookieProvider } from 'react-cookie'

const CookieYesContext = createContext({
  consentGiven: false,
  preferences: {},
  refreshConsent: () => {},
})

export const useCookieYes = () => useContext(CookieYesContext)

const CookieYesProvider = ({
  clientID = '257039f0ce7a6f6a35f76b27',
  blockUntilConsent = true,
  cookieOptions = { path: '/', maxAge: 365 * 24 * 60 * 60 }, // 1 year by default
  children,
}) => {
  const [consentGiven, setConsentGiven] = useState(false)
  const [preferences, setPreferences] = useState({})

  // Initialize CookieYes
  useEffect(() => {
    const script = document.createElement('script')
    script.src =
      'https://cdn-cookieyes.com/client_data/' + clientID + '/script.js'
    script.setAttribute('data-cookieyes', 'cookieyes-analytics')
    script.setAttribute('type', 'text/javascript')
    script.setAttribute('async', '')

    console.log('initializing cookie yes with clientID', clientID)
    if (blockUntilConsent) {
      script.setAttribute('data-block-cookies', 'true')
    }

    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [clientID, blockUntilConsent])

  // Set up listener for consent changes
  useEffect(() => {
    const handleConsentGiven = () => {
      try {
        if (window.CookieYes && window.CookieYes.consent) {
          const currentPreferences =
            window.CookieYes.consent.getUserPreferences()
          setPreferences(currentPreferences)
          setConsentGiven(true)
          console.log('CookieYes: Consent given', currentPreferences)
        }
      } catch (error) {
        console.error('Error retrieving CookieYes consent:', error)
      }
    }

    const checkInitialConsent = () => {
      try {
        if (window.CookieYes && window.CookieYes.consent) {
          const hasConsent = window.CookieYes.consent.hasUserConsent()
          if (hasConsent) {
            handleConsentGiven()
          }
        }
      } catch (error) {
        console.error('Error checking initial CookieYes consent:', error)
      }
    }

    // Add event listener for consent
    if (window.addEventListener) {
      window.addEventListener('cookieyes_consent_update', handleConsentGiven)

      // Check if consent was already given previously
      if (document.readyState === 'complete') {
        checkInitialConsent()
      } else {
        window.addEventListener('load', checkInitialConsent)
      }
    }

    return () => {
      if (window.removeEventListener) {
        window.removeEventListener(
          'cookieyes_consent_update',
          handleConsentGiven,
        )
        window.removeEventListener('load', checkInitialConsent)
      }
    }
  }, [])

  const refreshConsent = () => {
    try {
      if (window.CookieYes && window.CookieYes.consent) {
        const currentPreferences = window.CookieYes.consent.getUserPreferences()
        setPreferences(currentPreferences)
        setConsentGiven(window.CookieYes.consent.hasUserConsent())
      }
    } catch (error) {
      console.error('Error refreshing CookieYes consent:', error)
    }
  }

  return (
    <CookieYesContext.Provider
      value={{ consentGiven, preferences, refreshConsent }}
    >
      <ReactCookieProvider>{children}</ReactCookieProvider>
    </CookieYesContext.Provider>
  )
}

export default CookieYesProvider
